import { useEffect, useMemo, useState } from 'react';
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { CreditCard, ShieldCheck } from 'lucide-react';

import type { MapleMarketApp } from '@/data/mapleMarketApps';
import { formatMapleMarketPrice } from '@/data/mapleMarketApps';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';

const STRIPE_PUBLISHABLE_KEY = 'pk_live_51J1PlkHhjncHjxc7Wmua97kzLw2r8VUGPO8i8iXhU9lMR0yVsNkRY4BoHDRQUGDN54QVjK0be7ioaaVD8G5ghX5D00O5DiqLdA';
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

type MarketplacePurchaseDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: MapleMarketApp;
  onSuccess?: () => Promise<void> | void;
};

type MarketplacePurchaseFormProps = {
  clientSecret: string;
  product: MapleMarketApp;
  disabled?: boolean;
  onSuccess?: () => Promise<void> | void;
};

function MarketplacePurchaseForm({ clientSecret, product, disabled = false, onSuccess }: MarketplacePurchaseFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const { user, session } = useAuth();
  const [submitting, setSubmitting] = useState(false);

  const cardElementOptions = useMemo(
    () => ({
      style: {
        base: {
          color: '#111827',
          fontSize: '16px',
          iconColor: '#64748B',
          '::placeholder': {
            color: '#6B7280',
          },
        },
        invalid: {
          color: '#DC2626',
          iconColor: '#DC2626',
        },
      },
      hidePostalCode: true,
    }),
    []
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!user || !session?.access_token) {
      toast({
        title: 'Sign in required',
        description: 'Please sign in before buying Maple Market apps.',
        variant: 'destructive',
      });
      return;
    }

    if (!stripe || !elements) {
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      toast({
        title: 'Payment form not ready',
        description: 'Please wait a moment and try again.',
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);

    try {
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            email: user.email || undefined,
            name: user.user_metadata?.full_name || user.user_metadata?.name || undefined,
          },
        },
      });

      if (confirmError) {
        throw confirmError;
      }

      if (!paymentIntent?.id || paymentIntent.status !== 'succeeded') {
        throw new Error('Payment is still processing. Please try again in a moment.');
      }

      const { error: finalizeError } = await supabase.functions.invoke('finalize-marketplace-payment-intent', {
        body: {
          paymentIntentId: paymentIntent.id,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (finalizeError) {
        throw finalizeError;
      }

      window.dispatchEvent(new CustomEvent('maple-market:licenses-refresh'));
      toast({
        title: 'Purchase complete',
        description: `${product.name} is now available in your Maple Market library.`,
      });
      await onSuccess?.();
    } catch (error: any) {
      toast({
        title: 'Payment failed',
        description: error?.message || 'Please check your card details and try again.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="rounded-xl border border-border bg-white p-4 text-slate-900 shadow-sm">
        <div className="mb-3 flex items-center gap-2 text-sm font-medium">
          <ShieldCheck className="h-4 w-4 text-emerald-600" />
          Secure card payment powered by Stripe
        </div>
        <div className="rounded-lg border border-slate-200 bg-white px-3 py-3">
          <CardElement options={cardElementOptions} />
        </div>
      </div>

      <Button type="submit" className="w-full" disabled={disabled || submitting || !stripe || !elements}>
        <CreditCard className="mr-2 h-4 w-4" />
        {submitting ? 'Processing payment...' : `Pay ${formatMapleMarketPrice(product.price)}`}
      </Button>
    </form>
  );
}

export default function MarketplacePurchaseDialog({ open, onOpenChange, product, onSuccess }: MarketplacePurchaseDialogProps) {
  const { user, session } = useAuth();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [initializing, setInitializing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setClientSecret(null);
      setError(null);
      setInitializing(false);
      return;
    }

    if (!user || !session?.access_token || product.isFree) {
      return;
    }

    let cancelled = false;

    const createIntent = async () => {
      setInitializing(true);
      setError(null);

      const { data, error: invokeError } = await supabase.functions.invoke('create-marketplace-payment-intent', {
        body: {
          sku: product.sku,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (cancelled) {
        return;
      }

      if (invokeError || !data?.clientSecret) {
        setClientSecret(null);
        setError(invokeError?.message || data?.error || 'Unable to initialize payment.');
        setInitializing(false);
        return;
      }

      setClientSecret(data.clientSecret);
      setInitializing(false);
    };

    void createIntent();

    return () => {
      cancelled = true;
    };
  }, [open, product.isFree, product.sku, session?.access_token, user]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl border-border bg-background p-0 overflow-hidden">
        <div className="grid gap-0 md:grid-cols-[260px_1fr]">
          <div className="hidden h-full bg-muted md:block">
            <img src={product.image} alt={product.name} className="h-full w-full object-cover" />
          </div>
          <div className="p-6">
            <DialogHeader className="mb-6 text-left">
              <DialogTitle className="text-2xl">Buy {product.name}</DialogTitle>
              <DialogDescription>
                Complete your purchase without leaving MapleOS. Your license will be added to this account immediately after payment.
              </DialogDescription>
            </DialogHeader>

            <div className="mb-6 rounded-xl border border-border bg-card p-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-base font-semibold text-foreground">{product.name}</div>
                  <div className="text-sm text-muted-foreground">{product.developer}</div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-foreground">{formatMapleMarketPrice(product.price)}</div>
                  <div className="text-xs text-muted-foreground">One-time purchase</div>
                </div>
              </div>
            </div>

            <div className={cn('min-h-[180px]', initializing && 'flex items-center justify-center')}>
              {initializing ? (
                <div className="text-sm text-muted-foreground">Preparing secure payment...</div>
              ) : error ? (
                <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
                  {error}
                </div>
              ) : clientSecret ? (
                <Elements stripe={stripePromise} options={{ clientSecret }}>
                  <MarketplacePurchaseForm
                    clientSecret={clientSecret}
                    product={product}
                    onSuccess={async () => {
                      onOpenChange(false);
                      await onSuccess?.();
                    }}
                  />

                </Elements>
              ) : null}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
